import { Trans } from "@lingui/react";
import { Button } from "@warp-ds/react";
import DOMPurify from "isomorphic-dompurify";
import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon.tsx";
import { ScreenReaderSeparator } from "./ScreenReaderSeparator.tsx";

export const Description = ({
  className = "",
  trackClick,
  description,
}: {
  className?: string;
  trackClick: () => void;
  description: string | undefined;
}) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [canExpand, setCanExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded((prev) => {
      if (!prev) {
        trackClick();
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (descriptionRef.current && descriptionRef.current.getBoundingClientRect().height > 110) {
      setCanExpand(true);
    } else {
      setCanExpand(false);
    }
  }, []);

  if (!description) {
    return null;
  }

  const sanitizedDescription = DOMPurify.sanitize(description);

  return (
    <div className={`${className}`}>
      <div
        ref={descriptionRef}
        aria-describedby={canExpand ? "dealer-description" : undefined}
        className={`relative whitespace-pre-line ${expanded || !canExpand ? "max-h-none" : "max-h-[110px] overflow-hidden"}`}
      >
        {!expanded && canExpand && (
          <div
            className="absolute top-0 h-full w-full pointer-events-none"
            style={{ background: "linear-gradient(to top, var(--w-s-color-background-info-subtle), transparent)" }}
          />
        )}
        {sanitizedDescription}
      </div>
      {canExpand && (
        <div className={`relative s-bg-info-subtle`}>
          <Button
            onClick={toggleExpanded}
            quiet
            className="flex items-center gap-8"
            aria-expanded={expanded}
            aria-controls="dealer-description"
          >
            {expanded ? (
              <>
                <Icon name="Minus" />
                <ScreenReaderSeparator />
                <Trans id="dealer.description.showLess" message="Show less" />
              </>
            ) : (
              <>
                <Icon name="Plus" />
                <ScreenReaderSeparator />
                <Trans id="dealer.description.showDescription" message="Show entire description" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
